/**
 * OrdersPage — /orders
 * Buyer-facing list of paid orders. Each row is expandable to see items.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShoppingBag, Loader2, CheckCircle2, Building2 } from "lucide-react";

function fmt(amount, currency = "usd") {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(amount); }
  catch { return `$${amount.toFixed(2)}`; }
}

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await http.get("/orders/mine");
        setOrders(data.items || []);
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <ShoppingBag className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <h1 className="font-display text-2xl mb-2">Sign in to see your orders</h1>
      <Link to="/login?redirect=/orders" className="wofy-btn-primary inline-flex">Sign in</Link>
    </div>
  );

  if (loading) return <div className="max-w-3xl mx-auto py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;

  if (!orders.length) return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="orders-empty">
      <ShoppingBag className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <h1 className="font-display text-2xl mb-1">No orders yet</h1>
      <p className="opacity-70 mb-4">When you buy something on Social Pulse, it'll show up here.</p>
      <Link to="/businesses" className="wofy-btn-primary inline-flex">Explore businesses</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="orders-page">
      <h1 className="font-display text-4xl mb-6">My orders</h1>
      <ul className="space-y-3">
        {orders.map((o) => (
          <li key={o.id} className="wofy-card !p-4" data-testid={`order-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-xs opacity-70 font-mono">Order · {o.id.slice(0, 8).toUpperCase()}</div>
              <div className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" /> Paid
              </div>
            </div>
            <ul className="text-sm space-y-1">
              {(o.items || []).map((it, i) => (
                <li key={i} className="flex justify-between">
                  <span className="truncate">
                    {it.quantity} × {it.name}
                    {" "}
                    <Link to={`/business/${it.business_slug || ""}`} className="text-[11px] opacity-70 hover:underline">
                      <Building2 className="w-3 h-3 inline" /> {it.business_id?.slice(0,8)}
                    </Link>
                  </span>
                  <span className="opacity-70 tabular-nums">{fmt(it.unit_price * it.quantity, o.currency)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between mt-2 pt-2 border-t font-medium" style={{ borderColor: "var(--wofy-border)" }}>
              <span>Total</span>
              <span className="tabular-nums">{fmt(o.amount, o.currency)}</span>
            </div>
            <div className="text-[10px] opacity-60 mt-1">{new Date(o.created_at).toLocaleString()}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
